import { z } from "zod";
import { useTRPC } from "../../../trpc";
import { Button, Group, Select, Stack, TagsInput, Text, TextInput } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { useForm } from "@mantine/form";
import { useCallback, useMemo, useState, useEffect } from "react";
import { CopyCode } from "../../../components/CopyCode";
import { Alert } from "@mantine/core";
import { IconExclamationCircle, IconInfoCircle } from "@tabler/icons-react";
import { Link, LinkBack } from "../../../components/Link";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useLocation } from "@tanstack/react-router";
import type { TRPCClientErrorLike } from "@trpc/client";
import type { RootRouter, RouterOutputs } from "@project/api";
import { useMapkaNotification } from "../../../hooks/notifications.js";

type APIKey = RouterOutputs["createApiKey"];

interface NewKey {
  name: string;
  type: "public" | "secret" | "default";
  restrictions: string[];
}

const initialValues = {
  name: "",
  type: "public",
  restrictions: [],
} satisfies NewKey;

const URLSchema = z.string().superRefine((val: string, ctx) => {
  if (!val) {
    return;
  }

  const hasProtocol = val.includes("://");
  let protocol = "https";

  if (hasProtocol) {
    const [pro = ""] = val.split("://");
    protocol = pro;
  }

  if (protocol !== "http" && protocol !== "https") {
    ctx.addIssue({
      code: "custom",
      message: "Invalid protocol.",
      input: val,
    });
    return;
  }

  try {
    const url = new URL(hasProtocol ? val : `https://${val}`);
    const reDomain = /^([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

    if (!reDomain.test(url.hostname)) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid domain.",
        input: val,
      });
    }
  }
  catch {
    ctx.addIssue({
      code: "custom",
      message: "Invalid URL.",
      input: val,
    });
  }
});

export function APIKey() {
  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { data = [] } = useQuery(trpc.getApiKeys.queryOptions());
  const { mutate: createApikey, isPending, isSuccess } = useMutation(trpc.createApiKey.mutationOptions());
  const [newKey, setKey] = useState<APIKey | null>(null);
  const [error, setError] = useState<TRPCClientErrorLike<RootRouter> | null>(null);
  const mapkaNotification = useMapkaNotification();

  const editedApiKey: APIKey | undefined = (location.state as { apiKey?: APIKey }).apiKey;

  const { mutate: updateApiKey } = useMutation(trpc.updateApiKey.mutationOptions());

  const validate = useMemo(() => zod4Resolver(z.object({
    name: z
      .string()
      .min(2, intl.formatMessage({
        defaultMessage: "Name should have at least 2 characters",
        id: "error.nameTooShort2Chars",
      }))
      .refine (name => editedApiKey ?? data.every(key => key.name !== name),
        intl.formatMessage({
          id: "error.keyExists",
          defaultMessage: "Key with this name already exists",
        }),
      ),
    type: z.enum(["public", "secret", "default"]).default("public"),
    restrictions: z.array(URLSchema),
  })), [data, editedApiKey, intl]);

  const form = useForm({
    validateInputOnBlur: true,
    initialValues: editedApiKey ?? initialValues,
    validate,
  });
  const { onSubmit, getInputProps, values, setFieldValue } = form;

  // Clear restrictions when key type changes to secret
  useEffect(() => {
    if (values.type === "secret") {
      setFieldValue("restrictions", []);
    }
  }, [values.type, setFieldValue]);

  const handleEdit = useCallback((changedApiKey: NewKey) => {
    if (!editedApiKey) return;

    updateApiKey({ ...changedApiKey, id: editedApiKey.id }, {
      onSuccess: (key) => {
        setKey(key);
        void queryClient.invalidateQueries(trpc.getApiKeys.queryFilter());
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "API key edited",
            id: "notification.apiKeyEdited",
          }),
          message: intl.formatMessage({
            defaultMessage: "Successfully edited: {name}",
            id: "notification.apiKeyEditedMessage",
          }, { name: key.name }),
          variant: "success",
        });
      },
    });
  }, [updateApiKey, editedApiKey, queryClient, trpc, mapkaNotification, intl]);

  const handleSubmit = useCallback((newApiKey: NewKey) => {
    createApikey(newApiKey, {
      onSuccess(key) {
        setKey(key);
        void queryClient.invalidateQueries(trpc.getApiKeys.queryFilter());
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "Created API key",
            id: "notification.createdApiKey",
          }),
          message: intl.formatMessage({
            defaultMessage: "Created key: {name}",
            id: "notification.newApiKeyCreated",
          }, { name: key.name }),
          variant: "success",
        });
      },
      onError(err) {
        setError(err);
      },
    });
  }, [createApikey, mapkaNotification, intl, setKey, setError, queryClient, trpc]);

  return (
    <Stack px="md" my="md">
      <SimpleBreadcrumbs
        parent={(
          <LinkBack href="/$accountName/admin/api-keys">
            <FormattedMessage defaultMessage="API keys" id="navigation.apiKeys" />
          </LinkBack>
        )}
        current={editedApiKey ? intl.formatMessage({ defaultMessage: "Edit API key", id: "navigation.editApiKey" }) : intl.formatMessage({ defaultMessage: "New API key", id: "navigation.newApiKey" })}
      />
      {error && (
        <Alert variant="light" color="red" icon={<IconExclamationCircle />}>
          {error.message}
        </Alert>
      )}
      <form onSubmit={onSubmit(editedApiKey ? handleEdit : handleSubmit)}>
        <TextInput
          data-autofocus
          disabled={isPending || isSuccess}
          label={intl.formatMessage({ defaultMessage: "Name", id: "label.name" })}
          {...getInputProps("name")}
        />
        <Select
          disabled={isPending || isSuccess || !!editedApiKey}
          label={intl.formatMessage({ defaultMessage: "Type", id: "label.type" })}
          data={
            editedApiKey?.type === "default"
              ? [
                  { value: "public", label: intl.formatMessage({ defaultMessage: "Public", id: "label.public" }) },
                  { value: "secret", label: intl.formatMessage({ defaultMessage: "Secret", id: "label.secret" }) },
                  { value: "default", label: intl.formatMessage({ defaultMessage: "Default", id: "label.default" }) },
                ]
              : [
                  { value: "public", label: intl.formatMessage({ defaultMessage: "Public", id: "label.public" }) },
                  { value: "secret", label: intl.formatMessage({ defaultMessage: "Secret", id: "label.secret" }) },
                ]
          }
          {...getInputProps("type")}

        />
        {values.type !== "secret"
          && (
            <TagsInput
              data-autofocus
              disabled={isPending || isSuccess}
              label={intl.formatMessage({
                defaultMessage: "Urls",
                id: "label.urls",
              })}
              {...getInputProps("restrictions")}
            />
          )}

        {!editedApiKey && newKey && (
          <Stack gap="xs" pt="lg">
            <Text fw="bold">
              <FormattedMessage defaultMessage="New API key" id="text.newApiKey" />
            </Text>
            <CopyCode text={newKey.key} />

            {newKey.type == "secret" && (
              <Alert variant="light" color="yellow" icon={<IconInfoCircle />}>
                <FormattedMessage defaultMessage="For security reasons these token values will not be shown again." id="text.apiKeySecurity" />
              </Alert>
            )}
          </Stack>
        )}

        {editedApiKey && (
          <Stack gap="xs" pt="lg">
            <Text fw="bold">
              <FormattedMessage defaultMessage="API key" id="text.apiKey" />
            </Text>
            <CopyCode text={editedApiKey.key} />

            {editedApiKey.type == "secret" && (
              <Alert variant="light" color="yellow" icon={<IconInfoCircle />}>
                <FormattedMessage defaultMessage="For security reasons these token values will not be shown again." id="text.apiKeySecurity" />
              </Alert>
            )}
          </Stack>
        )}

        {!newKey && (
          <Group gap="xs" justify="flex-end" pt="lg">
            <Link href="/$accountName/admin/api-keys">
              <Button
                variant="secondary"
                type="reset"
                loading={isPending}
              >
                {editedApiKey
                  ? (
                      <FormattedMessage defaultMessage="Back to API keys" id="button.backToApiKeys" />
                    )
                  : (
                      <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
                    )}
              </Button>
            </Link>
            {editedApiKey && (
              <Button variant="tertiary" type="submit" loading={isPending}>
                <FormattedMessage defaultMessage="Save" id="button.save" />
              </Button>
            )}
            {!editedApiKey && (
              <Button variant="tertiary" type="submit" loading={isPending}>
                <FormattedMessage defaultMessage="Create" id="button.create" />
              </Button>
            )}
          </Group>
        )}
        {newKey && (
          <Group gap="xs" justify="flex-end" pt="lg">
            <Link href="/$accountName/admin/api-keys">
              <Button variant="secondary">
                <FormattedMessage defaultMessage="Back to API keys" id="button.backToApiKeys" />
              </Button>
            </Link>
          </Group>
        )}
      </form>
    </Stack>

  );
}
